import { type CSSProperties, type ReactNode } from "react";

/**
 * Sprint 16d — small presentational helpers shared across the wizard steps.
 * Kept separate from ConnectApiWizard so the step components don't import back
 * into the shell (which imports them) and create a cycle.
 */

export const wizardStyles = {
  intro: { fontSize: 12, color: "var(--color-text-secondary)", margin: "0 0 16px", lineHeight: 1.5 } as CSSProperties,
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--color-text-primary)",
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  } as CSSProperties,
  label: { fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)" } as CSSProperties,
  hint: { fontSize: 11, color: "var(--color-text-tertiary)", lineHeight: 1.5 } as CSSProperties,
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "7px 10px",
    fontSize: 13,
    fontFamily: "var(--font-sans)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
  } as CSSProperties,
  reason: { fontSize: 11, color: "var(--color-text-tertiary)", lineHeight: 1.5, fontStyle: "italic" } as CSSProperties,
};

export function WizardField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12, flex: 1, minWidth: 0 }}>
      <span style={wizardStyles.label}>{label}</span>
      {children}
      {hint && <span style={wizardStyles.hint}>{hint}</span>}
    </label>
  );
}

export function ConfidenceBadge({ confidence }: { confidence: "high" | "medium" | "low" }) {
  const map = {
    high: { c: "var(--c-green-800, var(--color-text-secondary))", bg: "var(--c-green-50, var(--color-background-secondary))" },
    medium: { c: "var(--c-amber-800, var(--color-text-secondary))", bg: "var(--c-amber-50, var(--color-background-secondary))" },
    low: { c: "var(--c-red-600, var(--color-text-secondary))", bg: "var(--c-red-50, var(--color-background-secondary))" },
  }[confidence];
  return (
    <span
      title={`AI confidence: ${confidence}`}
      style={{
        fontSize: 10,
        fontWeight: 500,
        color: map.c,
        background: map.bg,
        borderRadius: "var(--border-radius-sm)",
        padding: "1px 6px",
        textTransform: "uppercase",
        letterSpacing: "0.03em",
        flexShrink: 0,
      }}
    >
      {confidence}
    </span>
  );
}

const METHOD_COLORS: Record<string, string> = {
  GET: "var(--c-green-600, var(--color-text-info))",
  POST: "var(--c-blue-600, var(--color-text-info))",
  PUT: "var(--c-amber-800, var(--color-text-secondary))",
  PATCH: "var(--c-amber-800, var(--color-text-secondary))",
  DELETE: "var(--c-red-600, var(--color-text-secondary))",
};

export function MethodBadge({ method }: { method: string }) {
  const m = method.toUpperCase();
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        fontFamily: "var(--font-mono)",
        color: METHOD_COLORS[m] ?? "var(--color-text-tertiary)",
        border: "var(--kuvio-border-default)",
        borderRadius: "var(--border-radius-sm)",
        padding: "1px 6px",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {m}
    </span>
  );
}
